import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArchive, faRedo, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import ConfirmationPopup from './ConfirmationPopup';
import crmService from '../services/crmService';
import { dashboardService } from '../services/dashboardService';
import axiosInstance from '../utils/axiosConfig';
import { CRMTabIcon, CRMButtonIcon } from './ui';
import { useFlashMessage } from './FlashMessage';
import { getErrorMessage } from '../utils/errorMessages';
import Layout from './Layout';
import Footer from './ui/Footer';
import hmsLogoWhite from '../assets/images/IMT_logo.png';
import './CRMProfileScreen.css';

// Import individual tab components
import CRMPartnerProfileTab from './CRM/CRMPartnerProfileTab';
import CRMBankingDetailsTab from './CRM/CRMBankingDetailsTab';
import CRMContactManagementTab from './CRM/CRMContactManagementTab';
import CRMCommodityTradingTab from './CRM/CRMCommodityTradingTab';
import CRMKYCComplianceTab from './CRM/CRMKYCComplianceTab';
import CRMCreditRatingTab from './CRM/CRMCreditRatingTab';
import CRMDocumentHubTab from './CRM/CRMDocumentHubTab';
import CRMFinancialAccountingTab from './CRM/CRMFinancialAccountingTab';
import CRMRiskEngineTab from './CRM/CRMRiskEngineTab';
import CRMPartnerServicesTab from './CRM/CRMPartnerServicesTab';
import CRMAINotesTab from './CRM/CRMAINotesTab';
import CRMExternalDataTab from './CRM/CRMExternalDataTab';
import RFCConfirmationModal from './CRM/RFCConfirmationModal';
import RFCDetailsModal from './CRM/RFCDetailsModal';
import RFCHistoryModal from './CRM/RFCHistoryModal';
import RFCExpandableButton from './CRM/RFCExpandableButton';
import CRMDocumentUploadModal from './CRM/CRMDocumentUploadModal';
import { confirmName } from '../services/documentNamingService';
import AIProcessingWarningModal from './CRM/AIProcessingWarningModal';
import RecordLoadProblem, { describeLoadFailure } from './ui/RecordLoadProblem';

import PinToDeskButton from './mydesk/PinToDeskButton';
// Fields excluded from auto-save PATCH: AI-managed fields, read-only fields, and nested relation fields
const AUTO_SAVE_EXCLUDE_FIELDS = [
    // AI-managed fields
    'ai_risk_analysis', 'ai_notes_analysis_history', 'ai_external_data_analysis_history',
    'ai_accounting_analysis_report', 'ai_commodity_analysis_report', 'ai_enhanced_notes_report',
    'ai_external_intelligence_report', 'ai_kyc_analysis_report', 'ai_risk_analysis_report',
    'partner_overview_summary', 'partner_overview_source_urls', 'hk_sanction_screening_data', 'hk_individual_screening_data',
    'hk_screening_history', 'hk_suspect_flag', 'hk_highest_score', 'hk_last_screening_date',
    'hk_matched_lists', 'auto_sanction_check',
    // Read-only / computed fields
    'created_at_timestamp', 'updated_at_timestamp', 'profile_access_log',
    'profile_change_log', 'profile_review_history',
    'credit_balance',
    'sanction_screening_result', 'last_kyc_update_date', 'ultimate_beneficial_owner_info',
    'politically_exposed_persons_data', 'pending_rfc_count',
    // Nested relation fields (read-only from to_representation, not writable via PATCH)
    'crm_contacts', 'contacts', 'approval_logs', 'banking_details',
    'contact_profiles',
    // Read-only backend representations
    'bank_account_holder_addresses',
    // "Fill from Document" transient hand-off fields. These are consumed by
    // child tabs (Banking, Commodity) once and should never be auto-saved.
    // pending_document_analysis_id must only flow on EXPLICIT manual save so
    // DocuWare upload is tied to the user's commit, not a debounced timer.
    'extracted_banking_details', 'extracted_commodities', 'extracted_contacts', 'pending_document_analysis_id',
    // Server-owned approval state. Both are read_only in CRMProfileSerializer and
    // are set ONLY by the KYC review / mark-KYC-approved paths, which run the
    // approval transition, mint Debtor/Creditor numbers and write the audit row.
    // They are held in profileData purely so the screen can DISPLAY them, so they
    // must never be echoed back in a PATCH -- and updating them from a server
    // reply must not trip the auto-save timer.
    'kyc_status_current', 'approval_status',
];

// Archive Confirmation Modal Component
const ArchiveConfirmationModal = ({ 
    isOpen, 
    onConfirm, 
    onCancel, 
    partnerName,
    isArchived,
    isProcessing
}) => {
    if (!isOpen) return null;
    
    return (
        <div className="confirmation-popup-overlay">
            <div className="archive-modal">
                <div className="archive-modal-header">
                    <div className="archive-modal-icon">
                        <FontAwesomeIcon icon={isArchived ? faRedo : faArchive} />
                    </div>
                    <h2>{isArchived ? 'Reactivate Partner' : 'Archive Partner'}</h2>
                </div>
                
                <div className="archive-modal-content">
                    <p>
                        {isArchived 
                            ? `Are you sure you want to reactivate "${partnerName}"? They will return to the active partner list.`
                            : `Are you sure you want to archive "${partnerName}"? They will be hidden from the active partner list but can be reactivated later.`
                        }
                    </p>
                    {!isArchived && (
                        <div className="archive-modal-note">
                            <FontAwesomeIcon icon={faInfoCircle} />
                            <span>All partner data and documents will be preserved. You can reactivate this partner at any time.</span>
                        </div>
                    )}
                </div>
                
                <div className="archive-modal-actions">
                    <button 
                        onClick={onCancel}
                        disabled={isProcessing}
                        className="archive-modal-cancel"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={onConfirm}
                        disabled={isProcessing}
                        className={`archive-modal-confirm ${isArchived ? 'reactivate' : 'archive'}`}
                    >
                        {isProcessing ? (
                            <>
                                <span className="archive-spinner"></span>
                                {isArchived ? 'Reactivating...' : 'Archiving...'}
                            </>
                        ) : (
                            <>
                                <i className={`fas ${isArchived ? 'fa-redo' : 'fa-archive'}`}></i>
                                {isArchived ? 'Reactivate Partner' : 'Archive Partner'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Enhanced Partner Deletion Modal Component
const EnhancedDeleteConfirmationModal = ({ 
    isOpen, 
    onConfirm, 
    onCancel, 
    partnerName, 
    isDeleting,
    documentCounts,
    loadingCounts 
}) => {
    const [confirmationText, setConfirmationText] = useState('');
    const [showDetails, setShowDetails] = useState(false);
    
    if (!isOpen) return null;
    
    const isDeleteEnabled = confirmationText === partnerName && !isDeleting;
    const hasDocuments = documentCounts?.total_count > 0;
    
    return (
        <div className="confirmation-popup-overlay">
            <div className="enhanced-delete-modal">
                <div className="enhanced-delete-header">
                    <div className="enhanced-delete-icon">
                        !
                    </div>
                    <h2 style={{ color: 'white' }}>Delete Partner Profile</h2>
                </div>
                
                <div className="enhanced-delete-content">
                    <div className="enhanced-delete-warning">
                        <p><strong>This action cannot be undone!</strong></p>
                        <p>You are about to permanently delete:</p>
                        <ul>
                            <li>Partner profile: <strong>{partnerName}</strong></li>
                            <li>All contact information</li>
                            <li>All banking details</li>
                            <li>All trading relationships</li>
                            <li>All analysis history</li>
                            {loadingCounts ? (
                                <li>Loading - Counting associated documents...</li>
                            ) : hasDocuments ? (
                                <li><strong>{documentCounts.total_count} DocuWare documents</strong> across all cabinets</li>
                            ) : (
                                <li>No associated DocuWare documents found</li>
                            )}
                        </ul>
                    </div>
                    
                    {hasDocuments && !loadingCounts && (
                        <div className="enhanced-delete-documents">
                            <button 
                                type="button"
                                onClick={() => setShowDetails(!showDetails)}
                                className="enhanced-delete-toggle"
                            >
                                {showDetails ? 'Hide' : 'Show'} Document Breakdown
                            </button>
                            
                            {showDetails && (
                                <div className="enhanced-delete-breakdown">
                                    {documentCounts.breakdown.map((cabinet, index) => (
                                        <div key={index} className="cabinet-breakdown-item">
                                            <span className="cabinet-name">{cabinet.cabinet_name}</span>
                                            <span className="cabinet-count">
                                                {cabinet.error ? 'Error counting' : `${cabinet.document_count} files`}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    
                    <div className="enhanced-delete-recommendation">
                        <p><strong>Important:</strong> We strongly recommend downloading any important documents before deletion.</p>
                    </div>
                    
                    <div className="enhanced-delete-confirmation">
                        <label htmlFor="confirmationInput">
                            To confirm deletion, type the partner's exact name below:
                        </label>
                        <div className="expected-name">{partnerName}</div>
                        <input
                            id="confirmationInput"
                            type="text"
                            value={confirmationText}
                            onChange={(e) => setConfirmationText(e.target.value)}
                            placeholder="Type partner name exactly as shown above"
                            disabled={isDeleting}
                            autoFocus
                        />
                        {confirmationText && confirmationText !== partnerName && (
                            <div className="confirmation-error">
                                Name does not match exactly. Please check spelling and capitalization.
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="enhanced-delete-actions">
                    <button 
                        onClick={onCancel}
                        disabled={isDeleting}
                        className="enhanced-delete-cancel"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={onConfirm}
                        disabled={!isDeleteEnabled}
                        className="enhanced-delete-confirm"
                    >
                        {isDeleting ? (
                            <>
                                <span className="delete-spinner"></span>
                                Deleting...
                            </>
                        ) : (
                            'Delete Partner Profile'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

const CRMProfileScreen = () => {
    const { profileId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const { showError, showSuccess } = useFlashMessage();
    
    const [activeTab, setActiveTab] = useState('partner-profile');
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Row 125: why an existing profile didn't load. While set, the screen shows
    // RecordLoadProblem INSTEAD of the tabs. A failed load used to clear the
    // spinner and draw the empty editor, whose Save overwrote the partner.
    const [profileLoadProblem, setProfileLoadProblem] = useState(null);
    const [profileLoadAttempt, setProfileLoadAttempt] = useState(0);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showEnhancedDeleteModal, setShowEnhancedDeleteModal] = useState(false);
    const [showValidationPopup, setShowValidationPopup] = useState(false);
    const [showBackConfirmation, setShowBackConfirmation] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    
    // Enhanced deletion state
    const [documentCounts, setDocumentCounts] = useState(null);
    const [loadingDocumentCounts, setLoadingDocumentCounts] = useState(false);
    
    // Archive/Reactivate state
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    
    // CRM Approver permission
    const [isCRMApprover, setIsCRMApprover] = useState(false);
    const [isCreditApprover, setIsCreditApprover] = useState(false);
    const [isKycApprover, setIsKycApprover] = useState(false);
    
    // RFC (Request For Changes) state
    const [showRFCConfirmModal, setShowRFCConfirmModal] = useState(false);
    const [showRFCDetailsModal, setShowRFCDetailsModal] = useState(false);
    
    // Document Upload state
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    // When true, the document modal analyzes the partner's KYC documents on
    // file (portal + DocuWare) instead of asking for an upload.
    const [kycFillModalMode, setKycFillModalMode] = useState(false);
    const [pendingDocumentAnalysisId, setPendingDocumentAnalysisId] = useState(null);
    // AI document-name payload captured from the "Fill from Document" modal.
    // Confirmed against the DocuWare document id returned by the save.
    const [pendingNamingPayload, setPendingNamingPayload] = useState(null);
    const [showRFCHistoryModal, setShowRFCHistoryModal] = useState(false);
    const [rfcChanges, setRfcChanges] = useState({});
    const [isSubmittingRFC, setIsSubmittingRFC] = useState(false);
    const [pendingRFCs, setPendingRFCs] = useState([]);
    const [rfcHistory, setRfcHistory] = useState([]);
    const [originalProfileData, setOriginalProfileData] = useState(null);
    
    // Background AI Processing state
    const [backgroundProcesses, setBackgroundProcesses] = useState({
        riskAnalysis: {
            isProcessing: false,
            analysisId: null,
            startTime: null,
            result: null
        },
        documentScanning: {
            isProcessing: false,
            analysisId: null,
            fileName: null,
            startTime: null,
            result: null
        }
    });
    const [showAIProcessingWarning, setShowAIProcessingWarning] = useState(false);
    
    const formRef = useRef(null);
    const isInitialLoadRef = useRef(true);
    const autoSaveTimerRef = useRef(null);
    const autoSaveInFlightRef = useRef(false);
    const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

    const isNewProfile = profileId === 'new';
    // The partner in the form is the one in the address (not blank, not a
    // previous partner). Saving an existing profile needs this.
    const profileLoaded = Boolean(profileData?.id) && String(profileData.id) === String(profileId);

    const triggerAutoSave = useCallback((latestData) => {
        if (isNewProfile || !profileId || profileId === 'new') return;
        if (autoSaveInFlightRef.current) return;

        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }

        autoSaveTimerRef.current = setTimeout(async () => {
            if (autoSaveInFlightRef.current) return;
            if (isInitialLoadRef.current) return;
            // Row 125: never patch a partner whose record hasn't loaded here.
            if (!latestData?.id || String(latestData.id) !== String(profileId)) return;
            
            const patchData = { ...latestData };
            AUTO_SAVE_EXCLUDE_FIELDS.forEach(f => delete patchData[f]);
            delete patchData.created_by_employee_id;
            delete patchData.last_modified_by_employee_id;
            delete patchData.id;

            // Skip if the filtered payload has no meaningful fields left
            if (Object.keys(patchData).length === 0) return;

            autoSaveInFlightRef.current = true;
            setAutoSaveStatus('saving');
            try {
                await crmService.patchProfile(profileId, patchData);
                setAutoSaveStatus('saved');
                setHasUnsavedChanges(false);
                setTimeout(() => setAutoSaveStatus(null), 3000);
            } catch (err) {
                console.error('Auto-save failed:', err);
                setAutoSaveStatus('error');
                setTimeout(() => setAutoSaveStatus(null), 5000);
            } finally {
                autoSaveInFlightRef.current = false;
            }
        }, 5000);
    }, [isNewProfile, profileId]);

    // Cleanup auto-save timer on unmount
    useEffect(() => {
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, []);

    // Tab configuration with icons and labels
    const allTabs = [
        { id: 'partner-profile', label: 'Partner Profile', component: CRMPartnerProfileTab },
        { id: 'banking-details', label: 'Banking Details', component: CRMBankingDetailsTab },
        { id: 'contact-management', label: 'Contact Management', component: CRMContactManagementTab },
        { id: 'commodity-trading', label: 'Commodity Trading', component: CRMCommodityTradingTab },
        { id: 'kyc-compliance', label: 'KYC & Compliance', component: CRMKYCComplianceTab },
        { id: 'credit-rating', label: 'Credit Rating', component: CRMCreditRatingTab, requiresApprover: true },
        { id: 'document-hub', label: 'Document Hub', component: CRMDocumentHubTab, requiresApprover: true },
        { id: 'financial-accounting', label: 'Financial & Accounting', component: CRMFinancialAccountingTab, requiresApprover: true },
        { id: 'risk-engine', label: 'Risk Engine', component: CRMRiskEngineTab },
        { id: 'partner-services', label: 'Partner Services', component: CRMPartnerServicesTab },
        { id: 'ai-notes', label: 'AI Notes & Intelligence', component: CRMAINotesTab },
        { id: 'external-data', label: 'External Data', component: CRMExternalDataTab }
    ];
    
    // Filter tabs based on user permissions - Only CRM Approvers can see Document Hub and Financial & Accounting
    const tabs = allTabs.filter(tab => !tab.requiresApprover || isCRMApprover);

    // Default profile data for new profiles
    const defaultProfileData = {
        // Partner Profile Section
        legal_entity_name: '',
        trading_short_name: '',
        creditor_debtor_selections: ['Creditor', 'Debtor'],
        partner_type_selections: [],
        industry_selections: [],
        company_website_url: '',
        head_office_address_data: {},
        bank_account_holder_address_data: [],
        invoice_address_data: {},
        incorporation_date: '',
        business_categories: [],

        // Banking Details
        banking_details: [],
        payment_terms_as_debtor: [],
        payment_terms_as_creditor: [],
        vat_terms_selections: [],
        tax_registration_numbers: {},
        verified_banking_details_flag: false,

        // Contact Management
        contacts: [],

        // Commodity Trading
        traded_commodities: [],
        known_trade_partners: [],
        certificates_coa_specs: [],
        trading_stats: {
            historical_trade_volume: {},
            turnover_annual: {},
            trade_frequency: '',
            last_transaction_date: '',
            average_deal_size: {},
            project_links: ''
        },
        ai_commodity_analysis: {
            last_run: '',
            report_output: '',
            attachments: []
        },

        // KYC & Compliance (dummy data)
        kyc_status_current: 'Pending',
        approval_status: 'pending',
        sanction_screening_result: 'Clear',
        last_kyc_update: '',
        kyc_documents_uploaded: false,
        ubo_information: {},
        pep_status: false,
        aml_risk_level_current: 'Medium',
        ai_kyc_analysis: {
            last_run: '',
            report_output: ''
        },

        // Document Hub
        document_categories: {
            contracts: [],
            insurance_certificates: [],
            shipping_agreements: [],
            compliance_docs: [],
            expiry_reminders: [],
            all_partner_docs: []
        },

        // Financial & Accounting (dummy data)
        payment_history_summary: {},
        outstanding_balances: {
            payable: 0,
            receivable: 0
        },
        ai_accounting_analysis: {
            last_run: '',
            report_output: ''
        },

        // Risk Engine
        partner_risk_category: '',
        overall_risk_rating_score: 5,
        manual_risk_analysis: '',
        ai_risk_analysis: {
            last_run: '',
            risk_score: 5,
            summary: '',
            health_indication: 'Moderate',
            report_output: ''
        },

        // Partner Services
        services_provided: [],
        dependencies_linked_partners: [],

        // AI Notes & Intelligence
        ai_notes_input: '',
        ai_sentiment_analysis: {
            last_run: '',
            sentiment_report: '',
            action_points: [],
            concerns: [],
            opportunities: []
        },

        // External Data Intelligence
        external_data_report: {
            last_run: '',
            partner_news: [],
            esg_news: [],
            legal_disputes: [],
            report_output: ''
        },

        // Tracking fields - Remove employee IDs as these are set by backend
        created_at_timestamp: new Date().toISOString(),
        updated_at_timestamp: new Date().toISOString(),
        profile_access_log: [],
        profile_change_log: [],
        profile_review_history: []
    };

    // Fetch user's CRM approver status on mount
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const userInfo = await dashboardService.getUserInfo();
                setIsCRMApprover(userInfo.is_crm_approver || false);
                setIsCreditApprover(userInfo.is_credit_approver || false);
                // Mirrors crm.views._may_mark_kyc_approved EXACTLY. A narrower
                // rule here hides the button from people the server would let
                // through, which reads to them as a broken screen rather than a
                // missing grant (project memory: precheck-vs-enforcer drift).
                setIsKycApprover(
                    userInfo.is_superuser
                    || userInfo.is_kyc_approver
                    || userInfo.is_crm_approver
                    || userInfo.can_manage_crm_partners
                    || false
                );
            } catch (error) {
                console.error('Error fetching user info:', error);
                setIsCRMApprover(false);
                setIsCreditApprover(false);
                setIsKycApprover(false);
            }
        };
        fetchUserInfo();
    }, []);

    useEffect(() => {
        setProfileLoadProblem(null);

        const fetchProfileData = async () => {
            try {
                // CRITICAL: Reset both initial load flag AND unsaved changes when profileId changes
                // This ensures navigating between profiles (including from fuzzy search) starts clean
                isInitialLoadRef.current = true;
                setHasUnsavedChanges(false);
                setLoading(true);
                
                if (isNewProfile) {
                    // For new profiles, use default data
                    setProfileData(defaultProfileData);
                    setOriginalProfileData(defaultProfileData);
                } else {
                    // Fetch existing profile from API. The service's errors keep
                    // the server's status (row 127), which the "can't be opened"
                    // message below needs.
                    const profileData = await crmService.getProfile(profileId);
                    setProfileData(profileData);
                    setOriginalProfileData(JSON.parse(JSON.stringify(profileData))); // Deep clone
                    
                    // Fetch pending RFCs and history for this profile
                    try {
                        const rfcResponse = await crmService.getPendingChangeRequests(profileId);
                        setPendingRFCs(rfcResponse.change_requests || []);
                    } catch (rfcError) {
                        console.error('Error fetching pending RFCs:', rfcError);
                        // Don't block profile loading if RFC fetch fails
                        setPendingRFCs([]);
                    }
                    
                    try {
                        const historyResponse = await crmService.getChangeRequestHistory(profileId);
                        setRfcHistory(historyResponse.change_requests || []);
                    } catch (historyError) {
                        console.error('Error fetching RFC history:', historyError);
                        // Don't block profile loading if history fetch fails
                        setRfcHistory([]);
                    }
                }
                setError(null);
                
                // Allow enough time for all child components to mount and propagate data
                // before tracking changes as "unsaved" or triggering auto-save
                setTimeout(() => {
                    isInitialLoadRef.current = false;
                }, 3000);
            } catch (err) {
                console.error('Error fetching profile data:', err);
                setError(getErrorMessage(err, 'Failed to load profile data. Please try again.'));
                // Keep the answer: a refused or missing partner shows the
                // message, never the empty editor.
                if (!isNewProfile) {
                    setProfileLoadProblem(describeLoadFailure(err));
                }
            } finally {
                setLoading(false);
            }
        };

        fetchProfileData();
    }, [profileId, isNewProfile, profileLoadAttempt]);

    // Handle URL tab parameter
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const tabFromUrl = urlParams.get('tab');
        
        if (tabFromUrl && tabs.some(tab => tab.id === tabFromUrl)) {
            setActiveTab(tabFromUrl);
        }
    }, [location.search]);

    // CRITICAL: Cleanup document analysis on unmount if user navigates away without
    // saving. Applies to BOTH new profiles ("Create from Document") and existing
    // profiles ("Fill from Document") — if the user walks away without an
    // explicit Save, the temp file should not linger on disk.
    // On successful manual save, pendingDocumentAnalysisId is set to null before
    // this cleanup runs, so no spurious discard fires.
    useEffect(() => {
        return () => {
            if (pendingDocumentAnalysisId) {
                console.log('🧹 Cleaning up document analysis on unmount:', pendingDocumentAnalysisId);
                axiosInstance.delete(`/api/crm/documents/discard/${pendingDocumentAnalysisId}/`)
                    .then(() => console.log('✅ Document analysis cleaned up'))
                    .catch(err => console.error('❌ Failed to cleanup document analysis:', err));
            }
        };
    }, [pendingDocumentAnalysisId]);

    // Centralized polling manager for background AI processing
    useEffect(() => {
        let pollInterval;
        
        const pollBackgroundProcesses = async () => {
            try {
                // Poll risk analysis if processing
                if (backgroundProcesses.riskAnalysis.isProcessing) {
                    // Risk analysis doesn't have a progress endpoint, it completes immediately
                    // This is handled by the direct API call in the Risk Engine tab
                    // We'll just keep the state for navigation warning purposes
                }
                
                // Poll document scanning if processing
                if (backgroundProcesses.documentScanning.isProcessing && backgroundProcesses.documentScanning.analysisId) {
                    try {
                        const response = await axiosInstance.get(
                            `/api/crm/documents/progress/${backgroundProcesses.documentScanning.analysisId}/`
                        );
                        
                        const { is_complete, status, extracted_data, error_message } = response.data;
                        
                        if (is_complete) {
                            console.log('📄 Document scanning completed in background');
                            
                            if (status === 'completed' && extracted_data) {
                                // Store results and show flash message
                                setBackgroundProcesses(prev => ({
                                    ...prev,
                                    documentScanning: {
                                        ...prev.documentScanning,
                                        isProcessing: false,
                                        result: extracted_data
                                    }
                                }));
                                
                                // Show flash message if user is not on the modal
                                if (!showDocumentModal) {
                                    showSuccess('Document scanning completed! Review extracted data.');
                                }
                            } else if (status === 'failed') {
                                setBackgroundProcesses(prev => ({
                                    ...prev,
                                    documentScanning: {
                                        ...prev.documentScanning,
                                        isProcessing: false,
                                        result: null
                                    }
                                }));
                                showError(`Document scanning failed: ${error_message || 'Unknown error'}`);
                            }
                        }
                    } catch (error) {
                        console.error('Error polling document scanning:', error);
                        // Don't stop polling on network errors, just log them
                    }
                }
            } catch (error) {
                console.error('Error in background polling:', error);
            }
        };
        
        // Start polling if any process is active
        const hasActiveProcessing = backgroundProcesses.riskAnalysis.isProcessing || 
                                     backgroundProcesses.documentScanning.isProcessing;
        
        if (hasActiveProcessing) {
            console.log('🔄 Starting background polling');
            pollInterval = setInterval(pollBackgroundProcesses, 2000); // Poll every 2 seconds
        }
        
        return () => {
            if (pollInterval) {
                console.log('🛑 Stopping background polling');
                clearInterval(pollInterval);
            }
        };
    }, [backgroundProcesses, showDocumentModal, showSuccess, showError]);

    // Background process handlers
    const startRiskAnalysis = async (analysisData) => {
        console.log('🚀 Starting risk analysis in background');
        setBackgroundProcesses(prev => ({
            ...prev,
            riskAnalysis: {
                isProcessing: true,
                analysisId: analysisData.analysis_id,
                startTime: new Date(),
                result: null
            }
        }));
    };

    const completeRiskAnalysis = (result) => {
        console.log('✅ Risk analysis completed');
        setBackgroundProcesses(prev => ({
            ...prev,
            riskAnalysis: {
                ...prev.riskAnalysis,
                isProcessing: false,
                result: result
            }
        }));
        
        // Show flash message if user is not on risk engine tab
        if (activeTab !== 'risk-engine') {
            showSuccess('Risk analysis completed! View results in the Risk Engine tab.');
        }
    };

    const startDocumentScanning = (analysisId, fileName) => {
        console.log('🚀 Starting document scanning in background');
        setBackgroundProcesses(prev => ({
            ...prev,
            documentScanning: {
                isProcessing: true,
                analysisId: analysisId,
                fileName: fileName,
                startTime: new Date(),
                result: null
            }
        }));
    };

    const stopAllProcesses = () => {
        console.log('🛑 Stopping all background processes');
        setBackgroundProcesses({
            riskAnalysis: {
                isProcessing: false,
                analysisId: null,
                startTime: null,
                result: null
            },
            documentScanning: {
                isProcessing: false,
                analysisId: null,
                fileName: null,
                startTime: null,
                result: null
            }
        });
    };

    // Function to handle tab changes and update URL
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        // Update URL without causing a page reload
        const newUrl = `${location.pathname}?tab=${tabId}`;
        history.replace(newUrl);
    };

    const handleDataChange = (section, data) => {
        // DEBUG: Log banking details specifically
        if (section === 'banking_details_profiles') {
            console.log('CRMProfileScreen: Received banking details update from child:', {
                section,
                dataLength: data?.length || 0,
                data: data
            });
        }
        
        setProfileData(prev => {
            const updated = {
                ...prev,
                [section]: data,
                updated_at_timestamp: new Date().toISOString()
            };
            
            // Trigger auto-save for existing profiles, skip AI-managed and read-only sections
            if (!isInitialLoadRef.current && !isNewProfile && !AUTO_SAVE_EXCLUDE_FIELDS.includes(section)) {
                triggerAutoSave(updated);
            }
            
            return updated;
        });
        
        // Only set unsaved changes if not during initial load
        // This prevents false "unsaved changes" when navigating to existing profiles
        if (!isInitialLoadRef.current) {
            setHasUnsavedChanges(true);
        }
    };

    /**
     * Adopt server-authoritative state returned by an action endpoint.
     *
     * Distinct from handleDataChange, which means "the USER edited something" and
     * therefore marks the form dirty and schedules an auto-save. This means "the
     * SERVER has told us what the record now is", so it merges and stops.
     *
     * It exists because the mark-KYC-approved endpoint already returned the new
     * approval_status and the KYC tab threw it away, patching only the one field
     * it happened to name locally. The "Profile Approval Status" box then kept
     * showing the pre-click value until a full page reload -- the server was right
     * and the screen was stale. Passing the reply through here means any field the
     * endpoint returns now lands, including ones added later.
     */
    const handleServerStateChange = (patch) => {
        if (!patch || typeof patch !== 'object') return;
        const clean = Object.fromEntries(
            Object.entries(patch).filter(([, v]) => v !== undefined)
        );
        if (Object.keys(clean).length === 0) return;
        setProfileData(prev => (prev ? { ...prev, ...clean } : prev));
    };

    // === RFC HELPER FUNCTIONS ===
    
    /**
     * Calculate changes between original and current profile data
     * Returns an object with changed fields: {field_name: {old: value, new: value}}
     */
    const calculateChanges = (original, current) => {
        const changes = {};
        
        // Fields to compare (excluding read-only and metadata fields)
        const compareFields = [
            'legal_entity_name', 'trading_short_name', 'monday_account_number',
            'creditor_debtor_selections',
            'partner_type_selections', 'industry_selections', 'company_website_url',
            'head_office_address_data', 'invoice_address_data', 'incorporation_date',
            'business_categories', 'is_group_entity', 'payment_terms_as_debtor', 'payment_terms_as_creditor',
            'vat_terms_selections', 'tax_registration_numbers', 'verified_banking_details_flag',
            'manual_bank_verification_status', 'manual_verification_notes',
            'banking_details_active', 'deactivation_reason', 'kyc_status_current',
            'manual_ultimate_beneficial_owners', 'aml_risk_level_current',
            'project_links_text', 'historical_trade_volume_data', 'turnover_annual_data',
            'trade_frequency_data', 'last_transaction_date', 'average_deal_size_data',
            'partner_risk_category', 'overall_risk_rating_score', 'manual_risk_analysis_notes',
            'services_provided_selections', 'dependencies_linked_partners'
        ];
        
        // Compare simple fields
        compareFields.forEach(field => {
            const oldValue = original?.[field];
            const newValue = current?.[field];
            
            // Compare values (stringify for deep comparison)
            if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
                changes[field] = {
                    old: oldValue,
                    new: newValue
                };
            }
        });
        
        // Compare nested relationship fields
        const nestedFields = [
            'bank_account_holder_address_data',
            'banking_details_profiles',
            'contact_profiles',
            'traded_commodities',
            'known_trade_partners'
        ];
        
        nestedFields.forEach(field => {
            const oldValue = original?.[field] || [];
            const newValue = current?.[field] || [];
            
            if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
                changes[field] = {
                    old: oldValue,
                    new: newValue
                };
            }
        });
        
        return changes;
    };
    
    /**
     * Handle RFC confirmation - submit the RFC
     */
    const handleRFCConfirm = async () => {
        try {
            setIsSubmittingRFC(true);
            
            // Submit RFC
            const response = await crmService.submitChangeRequest(
                profileData.id,
                rfcChanges,
                '' // No notes for now - Phase 2 could add notes field
            );
            
            if (response.success) {
                showSuccess('Request For Changes submitted successfully! CRM Approvers will review your proposed changes.', 6000);
                
                // Close RFC modal
                setShowRFCConfirmModal(false);
                
                // Reload profile to discard changes and refresh data
                const refreshedProfile = await crmService.getProfile(profileData.id);
                setProfileData(refreshedProfile);
                setOriginalProfileData(JSON.parse(JSON.stringify(refreshedProfile)));
                setHasUnsavedChanges(false);
                
                // Refresh pending RFCs and history
                const rfcResponse = await crmService.getPendingChangeRequests(profileData.id);
                setPendingRFCs(rfcResponse.change_requests || []);
                
                const historyResponse = await crmService.getChangeRequestHistory(profileData.id);
                setRfcHistory(historyResponse.change_requests || []);
                
                // Reset RFC changes
                setRfcChanges({});
            }
        } catch (error) {
            console.error('Error submitting RFC:', error);
            showError(getErrorMessage(error, 'Failed to submit Request For Changes. Please try again.'), 6000);
        } finally {
            setIsSubmittingRFC(false);
        }
    };
    
    /**
     * Handle RFC cancellation
     */
    const handleRFCCancel = () => {
        setShowRFCConfirmModal(false);
        setRfcChanges({});
    };
    
    /**
     * Handle RFC Details button click
     */
    const handleRFCDetailsClick = () => {
        if (pendingRFCs.length > 0) {
            setShowRFCDetailsModal(true);
        }
    };
    
    /**
     * Handle RFC Details modal close
     */
    const handleRFCDetailsClose = () => {
        setShowRFCDetailsModal(false);
    };
    
    /**
     * Handle RFC History button click
     */
    const handleRFCHistoryClick = () => {
        setShowRFCHistoryModal(true);
    };
    
    /**
     * Handle RFC History modal close
     */
    const handleRFCHistoryClose = () => {
        setShowRFCHistoryModal(false);
    };

    /**
     * Handle RFC review submitted - refresh profile and pending RFCs
     */
    const handleRFCReviewSubmitted = async () => {
        try {
            // Refresh profile data
            const refreshedProfile = await crmService.getProfile(profileData.id);
            setProfileData(refreshedProfile);
            setOriginalProfileData(JSON.parse(JSON.stringify(refreshedProfile)));
            
            // Refresh pending RFCs and history
            const rfcResponse = await crmService.getPendingChangeRequests(profileData.id);
            setPendingRFCs(rfcResponse.change_requests || []);
            
            const historyResponse = await crmService.getChangeRequestHistory(profileData.id);
            setRfcHistory(historyResponse.change_requests || []);
        } catch (error) {
            console.error('Error refreshing data after RFC review:', error);
        }
    };

    // Validation function to check required fields
    const validateRequiredFields = (data) => {
        const errors = [];
        
        // Required field validations from Partner Profile tab
        if (!data.legal_entity_name || data.legal_entity_name.trim() === '') {
            errors.push('Legal Entity Name is required (Partner Profile tab)');
        }
        
        if (!data.business_categories || data.business_categories.length === 0) {
            errors.push('Business Category is required - Please select at least one: Bunkers or Cargo (Partner Profile tab)');
        }
        
        if (!data.partner_type_selections || data.partner_type_selections.length === 0) {
            errors.push('Type of Partner selection is required (Partner Profile tab)');
        }
        
        if (!data.industry_selections || data.industry_selections.length === 0) {
            errors.push('Industry selection is required (Partner Profile tab)');
        }
        
        if (!data.head_office_address_data || !data.head_office_address_data.formatted_address) {
            errors.push('Head Office Address is required (Partner Profile tab)');
        }
        
        // Validate Banking Details - if any banking details are added, Account Owner and Bank Name are required
        if (data.banking_details_profiles && data.banking_details_profiles.length > 0) {
            const incompleteBanks = data.banking_details_profiles.filter(bank => 
                !bank.account_owner || bank.account_owner.trim() === ''
            );
            if (incompleteBanks.length > 0) {
                errors.push('Account Owner is required for all banking details (Banking Details tab)');
            }
            
            const banksWithoutName = data.banking_details_profiles.filter(bank => 
                !bank.bank_name || bank.bank_name.trim() === ''
            );
            if (banksWithoutName.length > 0) {
                errors.push('Bank Name is required for all banking details (Banking Details tab)');
            }
        }
        
        // Validate Contacts - if any contacts are added, Contact Name is required
        if (data.contact_profiles && data.contact_profiles.length > 0) {
            const incompleteContacts = data.contact_profiles.filter(contact => 
                !contact.contact_full_name || contact.contact_full_name.trim() === ''
            );
            if (incompleteContacts.length > 0) {
                errors.push('Contact Name is required for all contacts (Contact Management tab)');
            }
        }
        
        // Validate Commodity Trading - if any commodities are added, Commodity Name is required
        if (data.traded_commodities && data.traded_commodities.length > 0) {
            const incompleteCommodities = data.traded_commodities.filter(commodity => 
                !commodity.commodity_name || commodity.commodity_name.trim() === ''
            );
            if (incompleteCommodities.length > 0) {
                errors.push('Commodity Name is required for all traded commodities (Commodity Trading tab)');
            }
        }
        
        // Note: known_trade_partners validation removed - it's now just an array of strings, no validation needed
        
        return errors;
    };

    // Handle banking verification changes after profile save
    // NOTE: Banking status changes (banking_details_active, manual_bank_verification_status) 
    // are now handled directly in the main profile update endpoint with audit logging.
    // This function is kept for any future banking-related post-save operations that 
    // might not be handled by the main update endpoint.
    const handleBankingVerificationSave = async (profileId, saveData) => {
        try {
            // REMOVED: Manual verification and banking deactivation calls
            // These are now handled directly in the main profile update endpoint
            // to avoid duplicate audit logging.
            
            console.log('🏦 Banking status changes handled by main profile update endpoint with audit logging');
            
        } catch (error) {
            console.error('❌ Error in banking verification save handler:', error);
            // Don't throw error to prevent disrupting the main save flow
        }
    };

    const handleSave = async (skipNavigation = false) => {
        // Cancel any pending auto-save to prevent race with manual save
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }

        // Row 125: an existing profile is only ever updated from its own loaded
        // record; anything else would overwrite the partner with blanks.
        if (!isNewProfile && !profileLoaded) {
            showError("This partner profile hasn't loaded, so it can't be saved. Reload the page and try again.", 6000);
            return;
        }

        // First validate required fields
        const validationErrors = validateRequiredFields(profileData);
        
        if (validationErrors.length > 0) {
            setValidationErrors(validationErrors);
            setShowValidationPopup(true);
            return; // Stop the save process
        }

        // RFC CHECK: For existing profiles, check if user can edit freely
        if (!isNewProfile && profileData && profileData.id) {
            try {
                const editPermissions = await crmService.canEditFreely(profileData.id);
                
                // If user CANNOT edit freely (non-CRM approver trying to edit approved profile)
                if (!editPermissions.can_edit_freely) {
                    // Calculate changes between original and current data
                    const changes = calculateChanges(originalProfileData, profileData);
                    
                    if (Object.keys(changes).length === 0) {
                        showError('No changes detected to submit for approval', 4000);
                        return;
                    }
                    
                    // Store changes and show RFC confirmation modal
                    setRfcChanges(changes);
                    setShowRFCConfirmModal(true);
                    return; // Stop normal save process
                }
            } catch (error) {
                console.error('Error checking edit permissions:', error);
                // If permission check fails, continue with normal save (fail open)
            }
        }

        // If validation passes, proceed with save
        try {
            setIsSaving(true);
            setError('');
            
            // Prepare data for saving with validation fixes
            const saveData = { ...profileData };
            
            // DEBUG: Log banking details in save data
            console.log('🏦 CRMProfileScreen: Save data banking details:', {
                banking_details_profiles: saveData.banking_details_profiles,
                banking_details_length: saveData.banking_details_profiles?.length || 0,
                full_save_data: saveData
            });
            
            // DEFENSIVE CHECK: Ensure banking details are not accidentally lost
            if (!saveData.banking_details_profiles) {
                console.warn('⚠️  Banking details missing from save data, initializing empty array');
                saveData.banking_details_profiles = [];
            }
            
            // Remove employee ID fields - these are set by backend based on authenticated user
            delete saveData.created_by_employee_id;
            delete saveData.last_modified_by_employee_id;
            
            // Transform known_trade_partners to the expected backend format
            if (saveData.known_trade_partners && Array.isArray(saveData.known_trade_partners)) {
                saveData.known_trade_partners = saveData.known_trade_partners.map(partner => {
                    // If it's already a string (partner name), convert to object format expected by backend
                    if (typeof partner === 'string') {
                        return {
                            trade_partner_name: partner,
                            relationship_type: ''
                        };
                    }
                    // If it's already an object, keep as is but ensure required fields exist
                    return {
                        trade_partner_name: partner.trade_partner_name || partner.partner_name || '',
                        relationship_type: partner.relationship_type || '',
                        ...(partner.id ? { id: partner.id } : {})
                    };
                });
            }
            
            // Fix URL format if needed
            if (saveData.company_website_url && !saveData.company_website_url.startsWith('http')) {
                saveData.company_website_url = `https://${saveData.company_website_url}`;
            }
            
            // Fix date format if needed - only process if date is not null/empty
            if (saveData.incorporation_date && saveData.incorporation_date !== null) {
                const dateStr = saveData.incorporation_date;
                if (typeof dateStr === 'string' && dateStr.includes('T')) {
                    saveData.incorporation_date = dateStr.split('T')[0];
                }
            } else {
                // Remove null/empty date values to prevent backend validation errors
                delete saveData.incorporation_date;
            }
            
            let savedProfile;
            const wasNewProfile = isNewProfile; // Capture the original state before save
            
            if (isNewProfile) {
                // Create new profile
                console.log('🏦 CRMProfileScreen: Creating new profile with banking details:', {
                    banking_details_count: saveData.banking_details_profiles?.length || 0,
                    banking_details: saveData.banking_details_profiles
                });
                
                // If creating from document, include analysis ID for synchronous DocuWare upload
                if (pendingDocumentAnalysisId) {
                    saveData.pending_document_analysis_id = pendingDocumentAnalysisId;
                    console.log('📤 Including document analysis ID for DocuWare upload:', pendingDocumentAnalysisId);
                }
                
                savedProfile = await crmService.createProfile(saveData);
                
                // CRITICAL FIX: Update URL to reflect the newly created profile ID
                // This prevents subsequent saves from creating duplicate profiles
                const newUrl = `/dashboard/crm/profile/${savedProfile.id}${location.search}`;
                history.replace(newUrl);
                
                // Auto sanction check result handling
                if (savedProfile.auto_sanction_check) {
                    const sc = savedProfile.auto_sanction_check;
                    if (sc.completed && sc.suspect) {
                        showError(`Sanction screening FLAGGED for "${savedProfile.legal_entity_name}" (score: ${sc.score}). Review required in KYC & Compliance tab.`, 10000);
                    } else if (sc.completed && !sc.suspect) {
                        showSuccess(`Sanction screening passed for "${savedProfile.legal_entity_name}" -- no matches found.`, 6000);
                    } else if (!sc.completed) {
                        showError(`Automatic sanction screening could not be completed: ${sc.error || 'Unknown error'}. Please run manually in KYC & Compliance tab.`, 8000);
                    }
                }
                
                console.log(`New profile created with ID ${savedProfile.id}, URL updated to prevent duplicates`);
            } else {
                // Update existing profile
                console.log('🏦 CRMProfileScreen: Updating profile with banking details:', {
                    profileId,
                    banking_details_count: saveData.banking_details_profiles?.length || 0,
                    banking_details: saveData.banking_details_profiles
                });

                // "Fill from Document" flow: include the pending analysis id so
                // the backend uploads the source file to DocuWare as part of
                // this save (mirrors the new-profile path). Only sent on
                // explicit manual save — auto-save deliberately omits this so
                // the user retains the option to discard the document by
                // navigating away before clicking Save Profile.
                if (pendingDocumentAnalysisId) {
                    saveData.pending_document_analysis_id = pendingDocumentAnalysisId;
                    console.log('📤 Including document analysis ID in update for DocuWare upload:', pendingDocumentAnalysisId);
                }

                savedProfile = await crmService.updateProfile(profileId, saveData);
            }
            
            setProfileData(savedProfile);
            setHasUnsavedChanges(false);
            
            // Cancel pending auto-save after successful manual save
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
                autoSaveTimerRef.current = null;
            }
            setAutoSaveStatus(null);
            
            // Handle banking verification changes after successful profile save
            await handleBankingVerificationSave(savedProfile.id, saveData);
            
            // DocuWare upload happens synchronously inside the backend create()
            // (new profile) or update() (Fill from Document on existing profile).
            // Clear the pending analysis id in both cases so the unmount cleanup
            // doesn't fire a spurious discard against an already-uploaded file.
            const docuwareUploadAttempted = Boolean(pendingDocumentAnalysisId);
            if (docuwareUploadAttempted) {
                console.log(`✅ Document analysis ${pendingDocumentAnalysisId} sent for DocuWare upload (context=${wasNewProfile ? 'create' : 'update'})`);
                setPendingDocumentAnalysisId(null);
            }

            // Confirm the user-reviewed AI document name now that the backend has
            // created the DocuWare document and returned its id. Non-blocking:
            // a failure here must never break the save (mirrors DocumentPanel).
            const namingPayload = pendingNamingPayload;
            if (savedProfile?.docuware_document_id && (namingPayload?.hasSuggestion || namingPayload?.isValid)) {
                try {
                    await confirmName({
                        docuwareDocumentId: savedProfile.docuware_document_id,
                        module: 'crm_profile_doc',
                        file: namingPayload.file || null,
                        typeDropdownValue: namingPayload.typeDropdownValue || '',
                        partnerName: namingPayload.partnerName || '',
                        relatedEntityId: savedProfile.id,
                        originalFilename: namingPayload.originalFilename || '',
                        components: namingPayload.components || {},
                        aiSuggestion: namingPayload.aiSuggestion || {},
                        userOverrides: namingPayload.userOverrides || [],
                        extractionConfidence: namingPayload.extractionConfidence ?? null,
                        isUserConfirmed: true,
                    });
                } catch (confirmErr) {
                    console.warn('confirmName failed for CRM profile document (non-blocking):', confirmErr);
                }
            }
            setPendingNamingPayload(null);

            // Show success message based on action type
            if (wasNewProfile) {
                const successMessage = docuwareUploadAttempted
                    ? `Partner profile "${savedProfile.legal_entity_name}" created and document uploaded successfully!`
                    : `Partner profile "${savedProfile.legal_entity_name}" created successfully!`;
                showSuccess(successMessage, 5000);
            } else {
                const successMessage = docuwareUploadAttempted
                    ? `Partner profile "${savedProfile.legal_entity_name}" updated and document stored in DocuWare!`
                    : `Partner profile "${savedProfile.legal_entity_name}" updated successfully!`;
                showSuccess(successMessage, 4000);
            }
            
            // CRITICAL FIX: Only navigate to CRM dashboard if this was the VERY FIRST save of a new profile
            // For subsequent saves or updates to existing profiles, stay on the current page
            if (wasNewProfile && !skipNavigation) {
                console.log('🚀 First save of new profile complete - navigating to CRM dashboard');
                setTimeout(() => {
                    history.push('/dashboard/crm');
                }, 500); // Small delay to show success state
            } else if (!skipNavigation) {
                console.log('📝 Profile updated successfully - staying on current page');
                // For existing profiles, we could optionally show a success message here
                // but don't navigate away from the profile
            }
            
        } catch (err) {
            console.error('Error saving profile:', err);
            
            // Handle validation errors specifically. An error the CRM service
            // already put into words (it keeps the server's answer as `cause`,
            // row 127) is shown as worded below, not re-read field by field.
            if (err.response && err.response.data && !err.cause) {
                const errors = err.response.data;
                const errorMessages = [];
                
                // Format validation errors
                Object.keys(errors).forEach(field => {
                    const fieldErrors = Array.isArray(errors[field]) ? errors[field] : [errors[field]];
                    fieldErrors.forEach(error => {
                        if (typeof error === 'string') {
                            errorMessages.push(`${field}: ${error}`);
                        } else if (error.message || error.detail) {
                            errorMessages.push(`${field}: ${error.message || error.detail}`);
                        }
                    });
                });
                
                // Use flash message for better user experience
                showError(`Failed to save profile: Validation failed: ${errorMessages.join(', ')}`, 8000);
                setError(`Validation errors: ${errorMessages.join(', ')}`); // Keep for backward compatibility
            } else {
                // Use flash message for general errors
                showError(getErrorMessage(err, 'Failed to save profile. Please try again.'), 8000);
                setError(getErrorMessage(err, 'Failed to save profile. Please try again.'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    // Handle document extraction results - PRE-FILL form fields
    const handleDocumentExtracted = ({ selectedData, analysisId, namingPayload, source }) => {
        console.log('📄 Document extracted, applying selected fields:', selectedData);

        if (source === 'kyc') {
            // Filled from KYC documents ALREADY stored (portal/DocuWare): there
            // is no new document to upload or name on save. Discard the
            // analysis record now (best-effort server-side cleanup).
            if (analysisId) {
                axiosInstance.delete(`/api/crm/documents/discard/${analysisId}/`)
                    .catch((error) => console.error('Failed to discard KYC fill analysis:', error));
            }
        } else {
            // Store analysis ID. For new profiles this drives the deferred DocuWare
            // upload on create. For existing profiles ("Fill from Document") it
            // drives the DocuWare upload on the next manual Save Profile.
            setPendingDocumentAnalysisId(analysisId);

            // Hold the AI document-name payload until the save resolves and we know
            // the DocuWare document id; confirmName is fired then (see handleSave).
            setPendingNamingPayload(namingPayload || null);
        }

        // For existing partners we MERGE into the current profileData:
        //   - scalar fields: apply whatever the user ticked (extraction modal
        //     pre-checks only empty fields by default, and warns on overwrite,
        //     so the user has already made the merge/overwrite decision)
        //   - array fields (partner types / industries): union, deduped
        //   - banking_details / commodities: handed off via the extracted_*
        //     side-channels — child tabs do per-row IBAN/name dedupe already
        // For new profiles, behavior is identical to before (no existing data
        // to merge with, so "merge" collapses to "set").
        setProfileData(prev => {
            const next = { ...prev };

            if (selectedData.legal_entity_name) {
                next.legal_entity_name = selectedData.legal_entity_name;
            }

            if (selectedData.trading_short_name) {
                next.trading_short_name = selectedData.trading_short_name;
            }

            if (selectedData.head_office_address) {
                next.head_office_address_data = {
                    formatted_address: selectedData.head_office_address,
                };
            }

            if (selectedData.invoice_address) {
                next.invoice_address_data = {
                    formatted_address: selectedData.invoice_address,
                };
            }

            if (selectedData.registration_number) {
                next.tax_registration_numbers = {
                    ...(prev.tax_registration_numbers || {}),
                    reg: selectedData.registration_number,
                };
            }

            if (selectedData.company_website_url) {
                next.company_website_url = selectedData.company_website_url;
            }

            if (selectedData.incorporation_date) {
                next.incorporation_date = selectedData.incorporation_date;
            }

            if (Array.isArray(selectedData.partner_type_selections)) {
                const existing = Array.isArray(prev.partner_type_selections) ? prev.partner_type_selections : [];
                next.partner_type_selections = Array.from(new Set([...existing, ...selectedData.partner_type_selections]));
            }

            if (Array.isArray(selectedData.industry_selections)) {
                const existing = Array.isArray(prev.industry_selections) ? prev.industry_selections : [];
                next.industry_selections = Array.from(new Set([...existing, ...selectedData.industry_selections]));
            }

            if (Array.isArray(selectedData.banking_details_parsed)) {
                next.extracted_banking_details = selectedData.banking_details_parsed.map(bank => ({
                    account_owner: bank.account_owner_name || '',
                    bank_name: bank.bank_name || '',
                    bank_address_data: bank.bank_address_data || {},
                    account_number: bank.bank_account_number || '',
                    iban: bank.iban_number || '',
                    swift: bank.swift_code || '',
                    bic: bank.bic_code || '',
                }));
            }

            if (Array.isArray(selectedData.contacts_parsed)) {
                next.extracted_contacts = selectedData.contacts_parsed.map(c => ({
                    contact_full_name: c.contact_full_name || '',
                    contact_role_title: c.contact_role_title || '',
                    contact_department: c.contact_department || '',
                    direct_phone_number: c.direct_phone_number || '',
                    mobile_phone_number: c.mobile_phone_number || '',
                    email_address: c.email_address || '',
                    is_primary_contact: !!c.is_primary_contact,
                }));
            }

            // The extraction modal exposes commodities under the `traded_commodities`
            // key (a list of commodity-name strings); the commodity tab consumes them
            // via the `extracted_commodities` side-channel. Read the key the modal
            // actually passes so ticked commodities land in the Commodity Trading tab.
            if (Array.isArray(selectedData.traded_commodities)) {
                next.extracted_commodities = selectedData.traded_commodities;
            }

            return next;
        });

        setShowDocumentModal(false);
        setKycFillModalMode(false);

        const fieldsCount = Object.keys(selectedData).filter(k => selectedData[k] !== null).length;
        const message = source === 'kyc'
            ? `${fieldsCount} fields filled from KYC documents on file. Review the changes and click Save Profile to commit.`
            : (isNewProfile
                ? `${fieldsCount} fields extracted! Review the data and click Save Profile to create the profile.`
                : `${fieldsCount} fields applied to "${profileData?.legal_entity_name || 'partner'}". Review the changes and click Save Profile to commit (and store the source document in DocuWare).`);
        showSuccess(message, 6000);
    };

    const handleBackClick = () => {
        // Check for active AI processing first
        const hasActiveProcessing = backgroundProcesses.riskAnalysis.isProcessing || 
                                     backgroundProcesses.documentScanning.isProcessing;
        
        if (hasActiveProcessing) {
            setShowAIProcessingWarning(true);
        } else if (hasUnsavedChanges) {
            setShowBackConfirmation(true);
        } else {
            history.push('/dashboard/crm');
        }
    };

    const handleBackConfirm = () => {
        history.push('/dashboard/crm');
        setShowBackConfirmation(false);
    };

    const handleBackCancel = () => {
        setShowBackConfirmation(false);
    };

    const handleAIProcessingWarningStay = () => {
        setShowAIProcessingWarning(false);
    };

    const handleAIProcessingWarningLeave = () => {
        // Stop all background processes
        stopAllProcesses();
        // Navigate away
        history.push('/dashboard/crm');
        setShowAIProcessingWarning(false);
    };

    const handleDeleteClick = () => {
        setShowEnhancedDeleteModal(true);
        // Start loading document counts when modal opens
        fetchDocumentCounts();
    };
    
    const fetchDocumentCounts = async () => {
        if (!profileData?.id) return;
        
        setLoadingDocumentCounts(true);
        try {
            const response = await axiosInstance.get(`/api/crm/profiles/${profileData.id}/document-counts/`);
            setDocumentCounts(response.data);
            console.log('Document counts fetched:', response.data);
        } catch (error) {
            console.error('Error fetching document counts:', error);
            showError('Failed to count associated documents');
            // Set default counts to allow deletion to proceed
            setDocumentCounts({
                total_count: 0,
                breakdown: [],
                partner_id: profileData.id,
                partner_name: profileData.legal_entity_name
            });
        } finally {
            setLoadingDocumentCounts(false);
        }
    };

    const handleEnhancedDeleteConfirm = async () => {
        try {
            setIsDeleting(true);
            setError('');
            
            console.log('Starting enhanced partner deletion for profile:', profileData.id);
            
            const response = await crmService.deleteProfile(profileData.id);
            
            if (response.data.success) {
                const deletedCount = response.data.deletion_details?.total_documents_deleted || 0;
                showSuccess(
                    `Partner profile "${profileData.legal_entity_name}" and ${deletedCount} associated documents deleted successfully!`, 
                    6000
                );
            } else {
                showSuccess(`Partner profile deleted successfully!`, 4000);
            }
            
            // Navigate back to CRM dashboard after successful delete
            history.push('/dashboard/crm');
            
        } catch (err) {
            console.error('Error deleting profile:', err);
            showError(getErrorMessage(err, 'Failed to delete profile. Please try again.'), 8000);
            setError(getErrorMessage(err, 'Failed to delete profile. Please try again.'));
        } finally {
            setIsDeleting(false);
            setShowEnhancedDeleteModal(false);
        }
    };

    const handleEnhancedDeleteCancel = () => {
        setShowEnhancedDeleteModal(false);
        setDocumentCounts(null);
        setLoadingDocumentCounts(false);
    };

    const handleDeleteConfirm = async () => {
        try {
            setIsDeleting(true);
            setError('');
            
            await crmService.deleteProfile(profileId);
            
            // Show success message
            showSuccess(`Partner profile deleted successfully!`, 4000);
            
            // Navigate back to CRM dashboard after successful delete
            history.push('/dashboard/crm');
            
        } catch (err) {
            console.error('Error deleting profile:', err);
            showError(getErrorMessage(err, 'Failed to delete profile. Please try again.'), 6000);
            setError(getErrorMessage(err, 'Failed to delete profile. Please try again.'));
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirmation(false);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirmation(false);
    };

    const handleArchiveClick = () => {
        setShowArchiveModal(true);
    };

    const handleArchiveConfirm = async () => {
        try {
            setIsArchiving(true);
            setError('');
            
            const isArchived = profileData?.is_archived;
            
            if (isArchived) {
                // Reactivate partner
                const response = await crmService.reactivateProfile(profileData.id);
                
                if (response.data.success) {
                    showSuccess(
                        `Partner "${profileData.legal_entity_name}" reactivated successfully!`, 
                        4000
                    );
                    // Refresh profile data
                    const updatedProfile = await crmService.getProfile(profileData.id);
                    setProfileData(updatedProfile);
                }
            } else {
                // Archive partner
                const response = await crmService.archiveProfile(profileData.id);
                
                if (response.data.success) {
                    showSuccess(
                        `Partner "${profileData.legal_entity_name}" archived successfully!`, 
                        4000
                    );
                    // Refresh profile data
                    const updatedProfile = await crmService.getProfile(profileData.id);
                    setProfileData(updatedProfile);
                }
            }
            
        } catch (err) {
            console.error('Error archiving/reactivating profile:', err);
            const action = profileData?.is_archived ? 'reactivate' : 'archive';
            showError(getErrorMessage(err, `Failed to ${action} profile. Please try again.`), 6000);
            setError(getErrorMessage(err, `Failed to ${action} profile. Please try again.`));
        } finally {
            setIsArchiving(false);
            setShowArchiveModal(false);
        }
    };

    const handleArchiveCancel = () => {
        setShowArchiveModal(false);
    };

    const handleValidationPopupConfirm = () => {
        setShowValidationPopup(false);
        
        // Determine which tab to navigate to based on the first error
        if (validationErrors.length > 0) {
            const firstError = validationErrors[0];
            
            if (firstError.includes('(Partner Profile tab)')) {
                setActiveTab('partner-profile');
            } else if (firstError.includes('(Banking Details tab)')) {
                setActiveTab('banking-details');
            } else if (firstError.includes('(Contact Management tab)')) {
                setActiveTab('contact-management');
            } else if (firstError.includes('(Commodity Trading tab)')) {
                setActiveTab('commodity-trading');
            } else {
                // Default to partner profile for any other errors
                setActiveTab('partner-profile');
            }
        } else {
            setActiveTab('partner-profile');
        }
    };

    const handleValidationPopupCancel = () => {
        setShowValidationPopup(false);
    };

    const ActiveTabComponent = tabs.find(tab => tab.id === activeTab)?.component;

    if (loading) {
        return (
            <Layout showLeftNavSidebar={true} showRightSidebar={false}>
                <div className="crm-profile-screen crm-loading">
                    <div className="crm-loading-container">
                        <div className="crm-loading-spinner"></div>
                        <p>Loading CRM profile...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (profileLoadProblem && !isNewProfile) {
        return (
            <Layout showLeftNavSidebar={true} showRightSidebar={false}>
                <div className="crm-profile-screen">
                    <RecordLoadProblem
                        problem={profileLoadProblem}
                        recordName="partner profile"
                        areaName="CRM"
                        backLabel="Back to CRM Dashboard"
                        onBack={handleBackConfirm}
                        onRetry={() => setProfileLoadAttempt((n) => n + 1)}
                    />
                </div>
            </Layout>
        );
    }

    // Error display now handled by flash messages, so skip the error container
    // if (error) {
    //     return (
    //         <Layout>
    //             <div className="crm-profile-screen crm-error">
    //                 <div className="crm-error-container">
    //                     <div className="crm-error-message">{error}</div>
    //                     <button 
    //                         className="crm-btn-secondary"
    //                         onClick={() => history.push('/dashboard/crm')}
    //                     >
    //                         Back to CRM Dashboard
    //                     </button>
    //                 </div>
    //             </div>
    //         </Layout>
    //     );
    // }

    return (
        <Layout showLeftNavSidebar={true} showRightSidebar={false}>
            <div className="crm-profile-screen">
                {/* IMT Header with Logo */}
                <div className="crm-profile-header">
                    {/* Back Button */}
                    <button 
                        className="crm-header-back-button"
                        onClick={handleBackClick}
                        title="Back to CRM Dashboard"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>

                    <div className="crm-header-left">
                        <img src={hmsLogoWhite} alt="IMT Bergbau AG" className="crm-header-logo-img" />
                        <div className="crm-title-content">
                            <div className="crm-title-row">
                                <h1 className="hms-screen-title">
                                    {isNewProfile 
                                        ? 'Create New CRM Profile' 
                                        : (profileData?.legal_entity_name || 'CRM Profile')
                                    }
                                </h1>
                                {!isNewProfile && pendingRFCs.length > 0 && (
                                    <span className="rfc-submitted-indicator-header">RFC submitted</span>
                                )}
                                {/* Set from HR (Create Partner / link): whose partner record this is */}
                                {!isNewProfile && profileData?.linked_employee_name && (
                                    <span className="crm-employee-indicator-header">
                                        Employee: {profileData.linked_employee_name}
                                    </span>
                                )}
                                {/* Added from a captured invoice, not yet approved */}
                                {!isNewProfile && profileData?.details_to_complete && (
                                    <span
                                        className="crm-details-to-complete-header"
                                        title="Added from a captured invoice with only what its PDF printed. Complete the details so the partner can be approved."
                                    >
                                        Details to complete
                                    </span>
                                )}
                            </div>
                            {!isNewProfile && profileData?.trading_short_name && (
                                <span className="crm-profile-subtitle">
                                    ({profileData.trading_short_name})
                                </span>
                            )}
                        </div>
                    </div>
                    
                    <div className="crm-header-right">
                        {hasUnsavedChanges && (
                            <span className="crm-unsaved-indicator">
                                Unsaved changes
                            </span>
                        )}
                        <div className="crm-action-buttons">
                            {!isNewProfile && <PinToDeskButton entityType="crm_profile" objectId={profileId} />}
                            {/* RFC Expandable Button - Shows when there are pending RFCs or history */}
                            {!isNewProfile && (
                                <RFCExpandableButton
                                    pendingCount={pendingRFCs.length}
                                    historyCount={rfcHistory.length}
                                    onPendingClick={handleRFCDetailsClick}
                                    onHistoryClick={handleRFCHistoryClick}
                                />
                            )}
                            <button 
                                className="crm-btn-secondary crm-document-upload-btn"
                                onClick={() => setShowDocumentModal(true)}
                                disabled={isSaving || isDeleting}
                                title={isNewProfile
                                    ? "Create profile from document using AI"
                                    : "Upload a document (KYC form, onboarding form, etc.) to fill in missing partner info using AI"}
                            >
                                <CRMButtonIcon name="upload" />
                                {isNewProfile ? 'Create from Document' : 'Fill from Document'}
                            </button>
                            {autoSaveStatus && !isNewProfile && (
                                <span style={{
                                    fontSize: '0.72rem',
                                    color: autoSaveStatus === 'saved' ? '#059669' : autoSaveStatus === 'error' ? '#dc2626' : '#6b7280',
                                    backgroundColor: autoSaveStatus === 'saved' ? '#f0fdf4' : autoSaveStatus === 'error' ? '#fef2f2' : '#f9fafb',
                                    border: `1px solid ${autoSaveStatus === 'saved' ? '#bbf7d0' : autoSaveStatus === 'error' ? '#fecaca' : '#e5e7eb'}`,
                                    borderRadius: '9999px',
                                    padding: '0.2rem 0.6rem',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.3rem',
                                    marginRight: '0.5rem',
                                    fontWeight: '500',
                                    transition: 'all 0.3s ease',
                                    opacity: autoSaveStatus ? 1 : 0
                                }}>
                                    {autoSaveStatus === 'saving' && <><span className="crm-btn-spinner" style={{ width: '10px', height: '10px' }}></span> Auto-saving...</>}
                                    {autoSaveStatus === 'saved' && <><i className="fas fa-check-circle" style={{ fontSize: '0.65rem' }}></i> Auto-saved</>}
                                    {autoSaveStatus === 'error' && <><i className="fas fa-exclamation-triangle" style={{ fontSize: '0.65rem' }}></i> Auto-save failed</>}
                                </span>
                            )}
                            <button
                                className="crm-btn-primary crm-save-btn"
                                onClick={handleSave}
                                disabled={isSaving || isDeleting}
                                data-tour="crm-profile-save"
                            >
                                {isSaving ? (
                                    <>
                                        <span className="crm-btn-spinner"></span>
                                        {isNewProfile ? 'Creating & Screening...' : 'Saving...'}
                                    </>
                                ) : (
                                    <>
                                        <CRMButtonIcon name="save" />
                                        Save Profile
                                    </>
                                )}
                            </button>
                            {!isNewProfile && !profileData?.is_archived && isCRMApprover && (
                                <button 
                                    className="crm-archive-btn archive"
                                    onClick={handleArchiveClick}
                                    disabled={isSaving || isArchiving}
                                    title="Archive Partner (CRM Approvers only)"
                                >
                                    {isArchiving ? (
                                        <span className="crm-btn-spinner"></span>
                                    ) : (
                                        <FontAwesomeIcon icon={faArchive} />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Archived Partner Banner */}
                {!isNewProfile && profileData?.is_archived && (
                    <div className="crm-archived-banner">
                        <FontAwesomeIcon icon={faArchive} />
                        <span>This partner is archived and hidden from active partner lists.</span>
                        {isCRMApprover && (
                            <button 
                                onClick={handleArchiveClick}
                                className="crm-archived-banner-action"
                                title="Reactivate Partner (CRM Approvers only)"
                            >
                                <FontAwesomeIcon icon={faRedo} />
                                Reactivate Partner
                            </button>
                        )}
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="crm-tab-navigation" data-tour="crm-profile-tabs">
                    <div className="crm-tabs-container">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                className={`crm-tab ${activeTab === tab.id ? 'active' : ''}`}
                                onClick={() => handleTabChange(tab.id)}
                            >
                                <CRMTabIcon tabId={tab.id} />
                                <span className="crm-tab-label">{tab.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div className="crm-tab-content">
                    {ActiveTabComponent && (
                        <ActiveTabComponent
                            profileData={profileData}
                            onDataChange={handleDataChange}
                            onServerStateChange={handleServerStateChange}
                            isNewProfile={isNewProfile}
                            hasUnsavedChanges={hasUnsavedChanges}
                            onSave={handleSave}
                            isSaving={isSaving}
                            isCreditApprover={isCreditApprover}
                            isKycApprover={isKycApprover}
                            // Additional props for Risk Engine tab (background processing)
                            {...(activeTab === 'risk-engine' && {
                                onStartRiskAnalysis: startRiskAnalysis,
                                onCompleteRiskAnalysis: completeRiskAnalysis,
                                riskAnalysisState: backgroundProcesses.riskAnalysis
                            })}
                            // KYC tab: fill missing profile details from the KYC
                            // documents already on file (existing partners only)
                            {...(activeTab === 'kyc-compliance' && !isNewProfile && {
                                onFillFromKycDocs: () => {
                                    setKycFillModalMode(true);
                                    setShowDocumentModal(true);
                                }
                            })}
                        />
                    )}
                </div>

                <HamsterFloatingButton />

                {/* Archive Confirmation Modal */}
                <ArchiveConfirmationModal
                    isOpen={showArchiveModal}
                    onConfirm={handleArchiveConfirm}
                    onCancel={handleArchiveCancel}
                    partnerName={profileData?.legal_entity_name || 'Unknown Partner'}
                    isArchived={profileData?.is_archived || false}
                    isProcessing={isArchiving}
                />

                {/* Enhanced Delete Confirmation Modal */}
                <EnhancedDeleteConfirmationModal
                    isOpen={showEnhancedDeleteModal}
                    onConfirm={handleEnhancedDeleteConfirm}
                    onCancel={handleEnhancedDeleteCancel}
                    partnerName={profileData?.legal_entity_name || 'Unknown Partner'}
                    isDeleting={isDeleting}
                    documentCounts={documentCounts}
                    loadingCounts={loadingDocumentCounts}
                />

                {/* Legacy Delete Confirmation Popup (kept for compatibility) */}
                {showDeleteConfirmation && (
                    <ConfirmationPopup
                        message={`Are you sure you want to delete "${profileData?.legal_entity_name || 'this profile'}"? This action cannot be undone and will permanently remove all associated data including contacts, banking details, and trading information.`}
                        onConfirm={handleDeleteConfirm}
                        onCancel={handleDeleteCancel}
                    />
                )}

                {/* Back Confirmation Popup */}
                {showBackConfirmation && (
                    <ConfirmationPopup
                        message={
                            <div>
                                <p style={{ marginBottom: '0.5rem', fontWeight: '500' }}>
                                    You have unsaved changes. Are you sure you want to leave?
                                </p>
                                <p style={{ margin: '0', color: '#666', fontSize: '0.9em' }}>
                                    All changes will be lost.
                                </p>
                            </div>
                        }
                        onConfirm={handleBackConfirm}
                        onCancel={handleBackCancel}
                        confirmButtonText="Leave Without Saving"
                        cancelButtonText="Stay on Page"
                    />
                )}

                {/* AI Processing Warning Modal */}
                <AIProcessingWarningModal
                    isOpen={showAIProcessingWarning}
                    onStay={handleAIProcessingWarningStay}
                    onLeave={handleAIProcessingWarningLeave}
                    processingTypes={[
                        ...(backgroundProcesses.riskAnalysis.isProcessing ? ['risk'] : []),
                        ...(backgroundProcesses.documentScanning.isProcessing ? ['document'] : [])
                    ]}
                />

                {/* Validation Popup */}
                {showValidationPopup && (
                    <ConfirmationPopup
                        message={
                            <div>
                                <p style={{ marginBottom: '1rem', fontWeight: '500' }}>
                                    Please complete the following required fields before saving:
                                </p>
                                <ul style={{ 
                                    listStyle: 'disc', 
                                    paddingLeft: '1.5rem', 
                                    margin: '0',
                                    textAlign: 'left'
                                }}>
                                    {validationErrors.map((error, index) => (
                                        <li key={index} style={{ marginBottom: '0.5rem' }}>
                                            {error}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        }
                        onConfirm={handleValidationPopupConfirm}
                        onCancel={handleValidationPopupCancel}
                        confirmButtonText={
                            validationErrors.length > 0 
                                ? validationErrors[0].includes('(Banking Details tab)') ? 'Go to Banking Details'
                                  : validationErrors[0].includes('(Contact Management tab)') ? 'Go to Contact Management'
                                  : validationErrors[0].includes('(Commodity Trading tab)') ? 'Go to Commodity Trading'
                                  : 'Go to Partner Profile'
                                : 'Go to Partner Profile'
                        }
                        cancelButtonText="Cancel"
                    />
                )}

                {/* RFC Confirmation Modal */}
                <RFCConfirmationModal
                    isOpen={showRFCConfirmModal}
                    onConfirm={handleRFCConfirm}
                    onCancel={handleRFCCancel}
                    profileName={profileData?.legal_entity_name || 'Unknown Partner'}
                    changeCount={Object.keys(rfcChanges).length}
                    isSubmitting={isSubmittingRFC}
                />

                {/* RFC Details Modal */}
                <RFCDetailsModal
                    isOpen={showRFCDetailsModal}
                    onClose={handleRFCDetailsClose}
                    rfcList={pendingRFCs} // Pass full array of pending RFCs
                    profileName={profileData?.legal_entity_name || 'Unknown Partner'}
                    isCRMApprover={isCRMApprover}
                    profileId={profileData?.id}
                    onReviewSubmitted={handleRFCReviewSubmitted}
                />
                
                {/* RFC History Modal */}
                <RFCHistoryModal
                    isOpen={showRFCHistoryModal}
                    onClose={handleRFCHistoryClose}
                    rfcHistory={rfcHistory}
                    profileName={profileData?.legal_entity_name || 'Unknown Partner'}
                />

                {/* CRM Document Upload Modal */}
                <CRMDocumentUploadModal
                    isOpen={showDocumentModal}
                    onClose={() => { setShowDocumentModal(false); setKycFillModalMode(false); }}
                    onSuccess={handleDocumentExtracted}
                    onStartDocumentScanning={startDocumentScanning}
                    documentScanningState={backgroundProcesses.documentScanning}
                    existingProfileData={isNewProfile ? null : profileData}
                    kycFillMode={kycFillModalMode}
                    kycPartnerId={profileData?.id || null}
                />

                {/* Hamster Chat Integration */}
                <HamsterFloatingButton />

                {/* Back to Top Button */}
                <BackToTopButton />

                {/* Footer */}
                <Footer />
            </div>
        </Layout>
    );
};

export default CRMProfileScreen; 